import { useEffect, useMemo, useState } from "react";
import { Frown, Meh, Smile, Laugh, Angry, Zap, Battery, BatteryLow, BatteryMedium, BatteryFull } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const MOODS = [
  { v: 1, label: "Very sad", icon: Angry,  color: "bg-rose-100 text-rose-700 border-rose-300" },
  { v: 2, label: "Sad",      icon: Frown,  color: "bg-orange-100 text-orange-700 border-orange-300" },
  { v: 3, label: "Okay",     icon: Meh,    color: "bg-amber-100 text-amber-800 border-amber-300" },
  { v: 4, label: "Good",     icon: Smile,  color: "bg-lime-100 text-lime-800 border-lime-300" },
  { v: 5, label: "Great",    icon: Laugh,  color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
];

const ENERGIES = [
  { v: 1, label: "Very low", icon: BatteryLow,    color: "bg-slate-100 text-slate-700 border-slate-300" },
  { v: 2, label: "Low",      icon: Battery,       color: "bg-blue-100 text-blue-800 border-blue-300" },
  { v: 3, label: "Medium",   icon: BatteryMedium, color: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  { v: 4, label: "Good",     icon: BatteryFull,   color: "bg-teal-100 text-teal-800 border-teal-300" },
  { v: 5, label: "High",     icon: Zap,           color: "bg-amber-100 text-amber-800 border-amber-300" },
];

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Mood() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mood, setMood] = useState(null);
  const [energy, setEnergy] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/moods");
      setEntries(data);
      const today = data.find((e) => e.date === todayStr());
      if (today) { setMood(today.mood); setEnergy(today.energy); setNote(today.note || ""); }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!mood || !energy) { toast.error("Please pick a mood and an energy level."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/moods", { date: todayStr(), mood, energy, note });
      setEntries((prev) => {
        const other = prev.filter((e) => e.date !== data.date);
        return [...other, data];
      });
      toast.success("Saved. Thanks for checking in.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const recent = useMemo(() => {
    return [...entries].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 7);
  }, [entries]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-slate-800">How I feel today</h1>
        <p className="text-slate-600 mt-1">Pick a face for your mood. Pick a battery for your energy.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="mood-form">
        <h2 className="font-heading font-bold text-lg text-slate-800 mb-3">My mood</h2>
        <div className="grid grid-cols-5 gap-2 mb-6">
          {MOODS.map(({ v, label, icon: Icon, color }) => (
            <button
              key={v}
              onClick={() => setMood(v)}
              data-testid={`mood-${v}`}
              className={`p-3 rounded-xl border flex flex-col items-center gap-1 ${color} ${mood === v ? "ring-2 ring-amber-400" : "opacity-90 hover:opacity-100"}`}
            >
              <Icon className="w-7 h-7" />
              <span className="text-xs font-semibold">{label}</span>
            </button>
          ))}
        </div>

        <h2 className="font-heading font-bold text-lg text-slate-800 mb-3">My energy</h2>
        <div className="grid grid-cols-5 gap-2 mb-6">
          {ENERGIES.map(({ v, label, icon: Icon, color }) => (
            <button
              key={v}
              onClick={() => setEnergy(v)}
              data-testid={`energy-${v}`}
              className={`p-3 rounded-xl border flex flex-col items-center gap-1 ${color} ${energy === v ? "ring-2 ring-amber-400" : "opacity-90 hover:opacity-100"}`}
            >
              <Icon className="w-7 h-7" />
              <span className="text-xs font-semibold">{label}</span>
            </button>
          ))}
        </div>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">A short note (optional)</span>
          <Textarea
            value={note} onChange={(e) => setNote(e.target.value)}
            className="mt-1 rounded-xl" rows={3}
            placeholder="What is on your mind today?"
            data-testid="mood-note-input"
          />
        </label>

        <div className="mt-5 flex justify-end">
          <Button onClick={save} disabled={saving} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="mood-save-btn">
            {saving ? "Saving..." : "Save today"}
          </Button>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="font-heading font-bold text-xl text-slate-800 mb-3">Last 7 check-ins</h2>
        {loading ? (
          <div className="text-slate-500">Loading...</div>
        ) : recent.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="mood-empty">
            No check-ins yet. Save today to get started.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="mood-history">
            {recent.map((e) => {
              const m = MOODS.find((x) => x.v === e.mood);
              const en = ENERGIES.find((x) => x.v === e.energy);
              return (
                <div key={e.id} className="bg-white rounded-2xl border border-slate-200 p-3 text-center">
                  <div className="text-xs text-slate-500">{e.date}</div>
                  <div className="mt-2 flex items-center justify-center gap-2">
                    {m && <m.icon className="w-6 h-6 text-slate-700" />}
                    {en && <en.icon className="w-6 h-6 text-slate-700" />}
                  </div>
                  {e.note && <p className="mt-2 text-xs text-slate-600 line-clamp-2">{e.note}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
